import type { ComponentType } from "react";
import type {
  ModuleDefinition,
  ModuleSubscribers,
  SlotProps,
} from "../../core/platform/registry/registry";
import type { CoreEventName } from "../../core/domain/events/bus";
import { db } from "../../core/data/local/db";
import { createDrawerModule, type DrawerHardware, type DrawerPrompts, type FailureCode } from "./drawer-module";
import { createTicketModule, type TicketDeps, type TicketPrompts } from "./ticket-module";
import { nextOrderNumber } from "./order-numbers";
import { TicketQueue } from "./ticket-queue";
import { renderTicket } from "./ticket-layout";
import { isTicketPrintingEnabled } from "./ticket-settings";
import { activeTransport } from "./select-transport";
import {
  drawerStatus,
  currentCapability,
  refreshCapability,
  resetCapability,
} from "./capability-state";
import { createDrawerRecorder } from "./recorder";
import { PeripheralSettings } from "./ui/PeripheralSettings";
import { TicketCloseoutSummary } from "./ui/TicketCloseoutSummary";
import { TicketOrderNumberBadge } from "./ui/TicketOrderNumberBadge";
import { WifiVoucherButton } from "./ui/WifiVoucherButton";

// Module-owned styles for both of this module's surfaces. Imported here rather
// than from main.tsx so core never carries a stylesheet for a feature it does
// not know exists — disabling the module leaves no orphaned CSS (SC-012).
import "./ui/peripherals.css";

/**
 * The peripherals module, assembled (T145, FR-108).
 *
 * This file is the COMPOSITION ROOT and nothing else. The trigger rules live in
 * `drawer-module.ts`, the hardware in `webusb-transport.ts` /
 * `webbluetooth-transport.ts`, the persistence in
 * `recorder.ts`; this only wires them together and hands the result to the
 * registry.
 *
 * WHY THE TRANSPORT IS SHARED RATHER THAN OWNED BY THE DRAWER. FR-108 requires
 * a future receipt-printing module to reuse this exact transport — it is the
 * same printer, on the same interface, and two modules each claiming it would
 * fight over it. So the transport is a module-level singleton behind an output
 * boundary, and printing later becomes another consumer rather than a second
 * connection.
 *
 * `activeTransport` is a FACADE over whichever bus this terminal uses (USB or
 * Bluetooth), so switching buses in settings does not leave the status monitor
 * polling a transport nobody writes to any more.
 *
 * NOTHING HERE EXPORTS `kick()`. The closed trigger list in `drawer-module.ts`
 * is the security control, and it only works if no other code can reach the
 * hardware. Adding a new way to open the drawer must mean editing that list, in
 * that file, visibly.
 */

const transport = activeTransport;

export { drawerStatus, currentCapability, refreshCapability };

/** Set by the host so a drawer event can be attributed to the open shift. */
let currentShiftId: string | null = null;
export function setDrawerShift(shiftId: string | null): void {
  currentShiftId = shiftId;
}

/**
 * Set by the host to show the manual-open dialog.
 *
 * Defaults to "not acknowledged" rather than to a silent success. If no host
 * has wired the prompt, the failure row still stands alone and no manual open
 * is invented — an unrecorded open is exactly what this must never produce.
 */
let promptHost: ((code: FailureCode) => Promise<boolean>) | null = null;
export function setManualOpenPrompt(
  prompt: ((code: FailureCode) => Promise<boolean>) | null,
): void {
  promptHost = prompt;
}

const hardware: DrawerHardware = {
  isSupported: () => transport.isSupported(),
  // `deviceGranted` is the durable "somebody paired a drawer here" fact: the
  // browser remembers a USB grant per origin across sessions.
  isConfigured: () => currentCapability().deviceGranted,
  isConnected: () => transport.isConnected(),
  // Local USB I/O. Deliberately awaits nothing on the network: a drawer opens
  // during a total outage exactly as it does online (FR-101, FR-144).
  kick: () => transport.kick(),
};

const prompts: DrawerPrompts = {
  requestManualAcknowledgement: async (reason) => {
    if (!promptHost) return false;
    return promptHost(reason);
  },
};

/**
 * The print queue and ticket subscribers (specs/002-barista-order-tickets).
 *
 * SHARES `transport`, NOT A SECOND CONNECTION — this is exactly what FR-108
 * anticipated the drawer's output boundary for.
 *
 * `canCut: false` for now: cut-capability detection is a real refinement
 * (contracts/peripheral-ticket.md notes it) but no functional requirement
 * blocks on it — FR-210's parenthetical fallback, feed clear of the tear bar,
 * is exactly what a conservative `false` here produces on every printer.
 */
export const ticketQueue = new TicketQueue({
  transport,
  render: (document) => renderTicket(document, { canCut: false }),
  shiftId: () => currentShiftId,
});

const ticketHardware = {
  isConfigured: () => isTicketPrintingEnabled() && currentCapability().deviceGranted,
};

/**
 * Set by the host to show the void/refund alert.
 *
 * Same "defaults to doing nothing rather than inventing an outcome" rule as
 * `promptHost` above — if no host is wired, the alert simply doesn't appear
 * rather than silently auto-acknowledging (FR-229).
 */
let voidAlertPromptHost: ((orderNumber: string) => Promise<void>) | null = null;
export function setVoidAlertPrompt(
  prompt: ((orderNumber: string) => Promise<void>) | null,
): void {
  voidAlertPromptHost = prompt;
}

const ticketPrompts: TicketPrompts = {
  requestVoidAcknowledgement: async (orderNumber) => {
    if (!voidAlertPromptHost) return;
    await voidAlertPromptHost(orderNumber);
  },
};

const ticketDeps: TicketDeps = {
  hardware: ticketHardware,
  queue: ticketQueue,
  cashierName: async (staffId) => (await db.staff.get(staffId))?.name ?? "Staff",
  orderNumber: (shiftId) => nextOrderNumber(shiftId),
};

/**
 * Combine two modules' handlers for the same event into one.
 *
 * `ModuleDefinition.subscribers` is one handler per event key, registered
 * under a single moduleId ("peripherals") — the registry has no concept of
 * two modules sharing an id. Drawer and ticket both react to `sale.finalized`
 * (and `sale.refunded`), so their handlers are composed here rather than one
 * silently overwriting the other. Each runs even if the other throws — a
 * jammed printer must never stop the drawer from opening, and a stuck drawer
 * must never stop a ticket from printing.
 */
function combineSubscribers(
  ...subscriberMaps: Array<ModuleSubscribers | undefined>
): ModuleSubscribers {
  const events = new Set<CoreEventName>();
  for (const map of subscriberMaps) {
    for (const key of Object.keys(map ?? {})) events.add(key as CoreEventName);
  }

  // Built loosely and cast once at the boundary: TypeScript has no sum type
  // for "a handler whose payload type varies per key of a Partial<Record<...>>
  // assigned inside a loop", the same reason PeripheralSettings is cast to
  // ComponentType<SlotProps> below rather than fought into shape.
  const combined: Record<string, (payload: unknown) => Promise<void>> = {};
  for (const event of events) {
    const handlers = subscriberMaps
      .map((map) => map?.[event])
      .filter((handler): handler is NonNullable<typeof handler> => Boolean(handler));

    combined[event] = async (payload: unknown) => {
      for (const handler of handlers) {
        try {
          await (handler as (e: unknown) => void | Promise<void>)(payload);
        } catch (error) {
          console.error(`[peripherals] a subscriber for "${event}" failed`, error);
        }
      }
    };
  }
  return combined as ModuleSubscribers;
}

export function createPeripheralsModule(): ModuleDefinition {
  const drawer = createDrawerModule({
    hardware,
    recorder: createDrawerRecorder(() => currentShiftId),
    prompts,
  });
  const ticket = createTicketModule(ticketDeps, ticketPrompts);

  return {
    id: "peripherals",
    name: "Cash drawer",
    version: "0.1.0",
    migrations: drawer.migrations ?? [],

    subscribers: combineSubscribers(drawer.subscribers, ticket.subscribers),

    slots: {
      // Cast because the slot contract is deliberately untyped per-slot: core
      // renders a module's component without knowing what it needs, which is
      // what keeps core from importing module types (Principle III).
      "till.settings": PeripheralSettings as ComponentType<SlotProps>,
      "till.shiftClose.summary": TicketCloseoutSummary as ComponentType<SlotProps>,
      "till.postSale": TicketOrderNumberBadge as ComponentType<SlotProps>,
      "terminal.actions": WifiVoucherButton as ComponentType<SlotProps>,
    },

    async onEnable() {
      // Probed at enable and again on every till load. A grant can be revoked,
      // a cable pulled, or a browser replaced between sessions, and a cached
      // "available" would promise drawer control the terminal cannot deliver.
      await refreshCapability();
      await ticketQueue.loadPending();
    },

    async onDisable() {
      await transport.disconnect();
      resetCapability();
    },
  };
}

export default createPeripheralsModule;
